import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState } from "react";
import { FileSearch, ExternalLink, Printer, Download, Search, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const COMMON_RHEUM_DRUGS = [
  "Methotrexate", "Hydroxychloroquine", "Sulfasalazine", "Leflunomide",
  "Adalimumab", "Etanercept", "Infliximab", "Rituximab", "Abatacept",
  "Tocilizumab", "Baricitinib", "Upadacitinib", "Tofacitinib",
  "Belimumab", "Mycophenolate Mofetil", "Azathioprine", "Ciclosporin",
  "Prednisolone", "Methylprednisolone", "Naproxen", "Diclofenac",
  "Etoricoxib", "Colchicine", "Allopurinol", "Febuxostat",
  "Denosumab", "Zoledronic Acid", "Alendronate",
];

interface SmpcResult {
  drugName: string;
  retrievedAt: string;
  emcUrl: string;
  mhraUrl: string;
  bnfUrl: string;
}

function buildResult(drugName: string): SmpcResult {
  return {
    drugName,
    retrievedAt: new Date().toLocaleString("en-GB", {
      day: "2-digit", month: "long", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
      timeZoneName: "short",
    }),
    emcUrl: `https://www.medicines.org.uk/emc/search/?q=${encodeURIComponent(drugName)}&type=13`,
    mhraUrl: `https://products.mhra.gov.uk/search/?search=${encodeURIComponent(drugName)}`,
    bnfUrl: `https://bnf.nice.org.uk/search/?q=${encodeURIComponent(drugName)}`,
  };
}

function handlePrint(result: SmpcResult) {
  const content = `SUMMARY OF PRODUCT CHARACTERISTICS (SmPC) REFERENCE
=====================================================
Drug: ${result.drugName}
Reference generated: ${result.retrievedAt}
Platform: RheumHub (UK Rheumatology Clinical Platform)

Official SmPC & Reference Sources:
EMC SmPC:              ${result.emcUrl}
MHRA Product Info:     ${result.mhraUrl}
BNF Search:            ${result.bnfUrl}

LEGAL NOTICE
The Summary of Product Characteristics (SmPC) is a regulatory document owned by the
Marketing Authorisation Holder (MAH). SmPC content is hosted by the Electronic Medicines
Compendium (medicines.org.uk), operated by Datapharm. This reference links to the
official SmPC. Always verify against the current BNF, SmPC, and local formulary
before dispensing, counselling, or making prescribing recommendations.

Source: Electronic Medicines Compendium (EMC) | medicines.org.uk
Copyright: Respective Marketing Authorisation Holders`;
  const w = window.open("", "_blank");
  if (w) {
    w.document.write(`<pre style="font-family:monospace;font-size:13px;padding:24px;white-space:pre-wrap;max-width:800px;">${content}</pre>`);
    w.document.close();
    w.focus();
    w.print();
  }
}

function handleDownload(result: SmpcResult) {
  const content = `SUMMARY OF PRODUCT CHARACTERISTICS (SmPC) REFERENCE
=====================================================
Drug: ${result.drugName}
Reference generated: ${result.retrievedAt}
Platform: RheumHub (UK Rheumatology Clinical Platform)

Official SmPC & Reference Sources:
EMC SmPC:              ${result.emcUrl}
MHRA Product Info:     ${result.mhraUrl}
BNF Search:            ${result.bnfUrl}

LEGAL NOTICE
The Summary of Product Characteristics (SmPC) is a regulatory document owned by the
Marketing Authorisation Holder (MAH). SmPC content is hosted by the Electronic Medicines
Compendium (medicines.org.uk), operated by Datapharm. This reference links to the
official SmPC. Always verify against the current BNF, SmPC, and local formulary
before dispensing, counselling, or making prescribing recommendations.

Source: Electronic Medicines Compendium (EMC) | medicines.org.uk
Copyright: Respective Marketing Authorisation Holders`;
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SmPC-reference-${result.drugName.replace(/\s+/g, "-")}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PharmacistSmpc() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<SmpcResult | null>(null);
  const [history, setHistory] = useState<SmpcResult[]>([]);

  const handleSearch = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const res = buildResult(trimmed);
    setResult(res);
    setHistory(prev => [res, ...prev.filter(h => h.drugName.toLowerCase() !== trimmed.toLowerCase())].slice(0, 10));
    setQuery(trimmed);
  };

  return (
    <PharmacistLayout title="SmPC Reference">
      <div className="space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-br from-violet-900 to-violet-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-violet-800/60 p-3 rounded-xl">
              <FileSearch className="h-7 w-7 text-violet-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">SmPC Reference Lookup</h2>
              <p className="text-violet-300 text-sm">Summary of Product Characteristics · UK MHRA Approved · EMC + BNF</p>
            </div>
          </div>
          <p className="text-violet-200 text-sm leading-relaxed max-w-3xl">
            Quickly access the MHRA-approved SmPC for any rheumatology medicine. Each lookup generates a timestamped record with direct links to the Electronic Medicines Compendium (EMC), MHRA product information, and the BNF — with print and export options for clinical governance records.
          </p>
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Search className="h-5 w-5 text-violet-600" /> Search Drug
          </h3>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="e.g. Methotrexate, Adalimumab, Baricitinib…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSearch(query); }}
              className="flex-1"
            />
            <Button onClick={() => handleSearch(query)} disabled={!query.trim()}
              className="bg-violet-700 hover:bg-violet-800 text-white">
              Look up SmPC
            </Button>
          </div>

          {/* Quick access */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Quick access</p>
            <div className="flex flex-wrap gap-2">
              {COMMON_RHEUM_DRUGS.map(drug => (
                <button key={drug} onClick={() => handleSearch(drug)}
                  className="text-xs bg-violet-50 text-violet-700 border border-violet-200 px-2.5 py-1 rounded-full hover:bg-violet-100 transition-colors">
                  {drug}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-white rounded-2xl shadow-sm border border-violet-200 p-8">
            <div className="flex items-start justify-between gap-3 mb-5">
              <div className="flex items-center gap-3">
                <div className="bg-violet-100 p-2 rounded-lg">
                  <FileSearch className="h-6 w-6 text-violet-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900">{result.drugName}</h3>
                  <p className="text-sm text-slate-500">Summary of Product Characteristics (SmPC)</p>
                </div>
              </div>
              <button onClick={() => setResult(null)} className="text-slate-400 hover:text-slate-700 transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Timestamp */}
            <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3 flex items-center gap-3 mb-5">
              <FileSearch className="h-4 w-4 text-violet-500 shrink-0" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-violet-800">SmPC reference generated</p>
                <p className="text-xs text-violet-600 font-mono mt-0.5">{result.retrievedAt}</p>
              </div>
              <span className="text-xs bg-violet-200 text-violet-800 px-2 py-0.5 rounded-full font-medium shrink-0">Timestamped</span>
            </div>

            {/* SmPC sections */}
            <div className="border border-slate-200 rounded-xl p-5 mb-5">
              <h4 className="text-sm font-bold text-slate-800 mb-3">SmPC Sections (view on EMC)</h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {[
                  "1. Name of the medicinal product",
                  "2. Qualitative & quantitative composition",
                  "3. Pharmaceutical form",
                  "4. Clinical particulars (indications, posology, contraindications, special warnings, interactions, pregnancy/lactation, effects on driving, undesirable effects, overdose)",
                  "5. Pharmacological properties (pharmacodynamics, pharmacokinetics, preclinical safety)",
                  "6. Pharmaceutical particulars (excipients, shelf life, storage, packaging)",
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="h-4 w-4 text-violet-400 mt-0.5 shrink-0" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-3">
              <a href={result.emcUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-violet-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-violet-800 transition-colors">
                <ExternalLink className="h-4 w-4" /> View SmPC on EMC
              </a>
              <a href={result.mhraUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-violet-300 hover:text-violet-700 transition-colors">
                <ExternalLink className="h-4 w-4" /> MHRA Product Info
              </a>
              <a href={result.bnfUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-violet-300 hover:text-violet-700 transition-colors">
                <ExternalLink className="h-4 w-4" /> BNF Search
              </a>
              <button onClick={() => handlePrint(result)}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-slate-400 transition-colors">
                <Printer className="h-4 w-4" /> Print / Save PDF
              </button>
              <button onClick={() => handleDownload(result)}
                className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-slate-400 transition-colors">
                <Download className="h-4 w-4" /> Export (.txt)
              </button>
            </div>
          </div>
        )}

        {/* Session history */}
        {history.length > 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
            <h3 className="font-bold text-slate-900 mb-3 text-sm">Session History</h3>
            <div className="space-y-2">
              {history.slice(1).map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{h.drugName}</p>
                    <p className="text-xs text-slate-400 font-mono">{h.retrievedAt}</p>
                  </div>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => setResult(h)}
                      className="text-xs text-violet-600 hover:text-violet-800 font-medium transition-colors">View</button>
                    <a href={h.emcUrl} target="_blank" rel="noopener noreferrer"
                      className="text-slate-400 hover:text-violet-600 transition-colors">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legal notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-900 mb-2">Legal Notice — Copyright & Attribution</h3>
              <p className="text-sm text-amber-800 leading-relaxed">
                The SmPC is a regulatory document owned by the <strong>Marketing Authorisation Holder (MAH)</strong>. SmPCs are publicly available on the <strong>Electronic Medicines Compendium (EMC)</strong> at <a href="https://www.medicines.org.uk" target="_blank" rel="noopener noreferrer" className="underline font-medium">medicines.org.uk</a>, operated by Datapharm Communications Ltd. RheumHub links to the official SmPC only — SmPC text is not reproduced on this platform. Always verify against the current BNF, SmPC, and local formulary before dispensing or making prescribing recommendations.
              </p>
              <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap gap-4 text-xs text-amber-700">
                <span>Source: EMC | medicines.org.uk</span>
                <span>Operator: Datapharm Communications Ltd</span>
                <span>© Respective Marketing Authorisation Holders</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </PharmacistLayout>
  );
}
